const timeFilter = require('../../../../utils/timestamp-util').timestampToDateFilter;

const DATA_TYPE = {
    Int8: "Int8",
    UInt8: "UInt8",
    Int16: "Int16LE",
    UInt16: "UInt16LE",
    Int32LE: "Int32LE",
    UInt32LE: "UInt32LE",
};

function fixFloat(float, fractionDigits = 1) {
    return parseFloat(float.toFixed(fractionDigits));
}

const IPSO_PARSER = {
    0: parseSimpleData("digital_input", DATA_TYPE.UInt8),
    1: parseSimpleData("digital_output", DATA_TYPE.UInt8),
    2: parseSimpleData("analog_input", DATA_TYPE.Int16, value => fixFloat(value * 0.01)),
    3: parseSimpleData("analog_output", DATA_TYPE.Int16, value => fixFloat(value * 0.01)),
    101: parseSimpleData("illumiance_sensor", DATA_TYPE.UInt16),
    102: parseSimpleData("presence_sensor", DATA_TYPE.UInt8),
    103: parseSimpleData("temperature_sensor", DATA_TYPE.Int16, value => fixFloat(value * 0.1)),
    104: parseSimpleData("humidity_sensor", DATA_TYPE.UInt8, value => fixFloat(value * 0.5)),
    116: parseSimpleData("voltage", DATA_TYPE.UInt16, value => fixFloat(value * 0.01)),
    117: parseSimpleData("current", DATA_TYPE.UInt8),
    124: parseSimpleData("loudness", DATA_TYPE.UInt16, value => fixFloat(value * 0.1)),
    128: parseSimpleData("power", DATA_TYPE.UInt16, value => fixFloat(value * 0.01)),
};

const IPSO_TYPE = {
    0: "digital_input",
    1: "digital_output",
    2: "analog_input",
    3: "analog_output",
    101: "illumiance_sensor",
    102: "presence_sensor",
    103: "temperature_sensor",
    104: "humidity_sensor",
    106: "actuation",
    115: "barometer_pressure",
    116: "voltage",
    117: "current",
    119: "depth",
    123: "pressure",
    124: "loudness",
    125: "concentration",
    128: "power",
    130: "distance",
    146: "par",
    200: "counter",
};

const URSA_PARSER = {
    1: parseSimpleData("version", DATA_TYPE.UInt8, value => value.toString()),
    2: parseSimpleData("interval", DATA_TYPE.UInt16),
    3: parseSimpleData("period", DATA_TYPE.UInt16),
    4: skip(1, "response"), // response
    5: parseLoRaChanMask,
    6: parseAlarmConfig, // alarmConfig
    7: skip(1, "debug_level"), // debugLevel
    8: parseSn, // sn
    9: parseVersion("hardware"),
    10: parseVersion("firmware"),
    11: parseSimpleData("power_on", DATA_TYPE.UInt8, value => value > 0),
    12: parseSimpleData("power_off", DATA_TYPE.UInt8, value => value > 0),
    13: parseAlarm,
    15: parseSimpleData("class", DATA_TYPE.UInt8, value => ["ClassA","ClassB","ClassC"][value]),
    16: parseSimpleData("reboot", DATA_TYPE.UInt8),
    17: parseSimpleData("timestamp", DATA_TYPE.UInt32LE, timeFilter),
    18: parseText,
    19: parseSimpleData("temperature_intelligent_report", DATA_TYPE.UInt8),
    20: parseConversion,
};

const URSA_TYPE = {
    1: "version",
    2: "interval",
    3: "period",
    4: "response",
    5: "LoRa_chan_mask",
    6: "alarm_config",
    7: "debug_level",
    8: "sn",
    9: "hardware",
    10: "firmware",
    11: "power_on",
    12: "power_off",
    13: "temperature_alarm_report",
    14: "channels",
    15: "class",
    16: "reboot",
    17: "timestamp",
    18: "text",
    19: "temperature_intelligent_report",
    20: "conversion",
};

function parse(reader) {
    let i = 1;
    while (reader.hasMore()) {
        const channel = reader.readUInt8(`${i}.channel`);
        const type = reader.readUInt8(`${i}.type`, type => channel !== 255 ? IPSO_TYPE[type] : URSA_TYPE[type]);
        const parser = getReadParser(channel, type);
        if (parser) {
            parser.parse(reader, i);
            i++;
        }
    }
}

function getReadParser(channel, type) {
    if (channel === 254 || channel === 255) {
        return {parse: URSA_PARSER[type]};
    } else {
        return {parse: IPSO_PARSER[type]};
    }
}

function parseSimpleData(property, dataType, converter = value => value) {
    return (reader, index) => {
        reader[`read${dataType}`](`${index}.data`, converter);
    };
}

function skip(length) {
    return reader => {
        reader.moveOffset(length);
    };
}

function parseLoRaChanMask(reader, index) {
    reader.readUInt8(`${index}.data.id`);
    reader.readUInt16LE(`${index}.data.mask`);
}

function parseText(reader, index) {
    const length = reader.readUInt8(`${index}.data.length`);
    reader.readString(length, `${index}.data.text`);
}

function parseConversion(reader, index) {
    const value = reader.readUInt8(`${index}.data.index`);
    reader.result.set(`${index}.data.index`, (value & 240) >>> 4);
    reader.result.set(`${index}.data.type`, (value & 15) === 0 ? "mA" : "V");
}

function parseAlarmConfig(reader, index) {
    const MODE = ["DISABLE", "BELOW", "ABOVE", "WITHIN", "BELOW_OR_ABOVE"];
    const idAndMode = reader.readUInt8(`${index}.data.rule_id`, idAndMode => (idAndMode & 56) >>> 3);
    const mode = MODE[idAndMode & 7];
    reader.result.set(`${index}.data.mode`, mode);
    reader.readInt16LE(`${index}.data.min`, value => value / 10);
    reader.readInt16LE(`${index}.data.max`, value => value / 10);
    reader.readInt16LE(`${index}.data.lock_time`);
    reader.readInt16LE(`${index}.data.continue_time`);
}

const ALARM_MODE = ["DISABLE", "BELOW", "ABOVE", "WITHIN", "BELOW_OR_ABOVE"];
function parseAlarm(reader, index) {
    const idAndMode = reader.readUInt8(`${index}.data.rule_id`, idAndMode => (idAndMode & 56) >>> 3);
    const mode = ALARM_MODE[idAndMode & 7];
    reader.result.set(`${index}.data.mode`, mode);
    reader.readInt16LE(`${index}.data.min`, value => value / 10);
    reader.readInt16LE(`${index}.data.max`, value => value / 10);
    reader.readInt16LE(`${index}.data.current`, value => value / 10);
}

function parseSn(reader, index) {
    reader.readHexString(6, `${index}.data.sn`);
}

function parseVersion(property) {
    return (reader, index) => {
        const mainVer = reader.readUInt8(`${index}.data`);
        const mainVerHex = reader.result.get(`${index}.data`, 1);
        const subVer = reader.readUInt8(`${index}.data`);
        const subVerHex = reader.result.get(`${index}.data`, 1);
        const realSubVer = subVer % 16 === 0 && property === "hardware" ? Math.floor(subVer / 16) : subVer % 16 + Math.floor(subVer / 16) * 10;
        const realMainVer = mainVer % 16 + Math.floor(mainVer / 16) * 10;
        reader.setAllToResult(`${index}.data`, [`v${realMainVer}.${realSubVer}`, mainVerHex + subVerHex]);
    };
}

module.exports.parse = parse;



// WEBPACK FOOTER //
// ./src/lib/ucp/parsers/mode/ipso/v1/t1.js